import React, { useCallback, useState } from 'react';
import cn from 'classnames';

import ICO_UP_DOWN from 'assets/images/icons/arrows/arrow-up-down.svg';
import Button from 'components/form/Button.component';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import createFullArray from 'utils/arrays/createFullArray.util';
import moveArrayElement from 'utils/arrays/moveArrayElement.util';
import call from 'utils/functions/call.util';
import createNodeKey from 'utils/react/createNodeKey.util';

import CN from './InteractiveTable.module.scss';

const USE_RENDER_MOVE_DOWN_BUTTON = false;

const InteractiveTable = ({ data, columns, onAdd }) => {
  const lenColumns = columns?.length;

  const { isDarkMode } = useTheme();
  const [tableData, setTableData] = useState(data);

  const handleMoveUp = useCallback(
    (ev) => void setTableData(moveArrayElement({ array: tableData, index: ev?.rowIndex, moveBy: +1 })),
    [tableData],
  );

  const handleMoveDn = useCallback(
    (ev) => void setTableData(moveArrayElement({ array: tableData, index: ev?.rowIndex, moveBy: -1 })),
    [tableData],
  );

  const addNewRow = useCallback(
    ({ index }) => {
      const result = call('InteractiveTable.onAdd', onAdd, { index });
      const values = createFullArray({ item: result?.cell, size: lenColumns });

      setTableData([...tableData, { category: result.first, values }]);
    },
    [lenColumns, onAdd, tableData],
  );

  return (
    <div
      data-bem="InteractiveTable"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.outer}>
        <div className={CN.tableContainer}>
          <div className={CN.tableHeader} data-bem="InteractiveTable__header">
            <div className={CN.headerFixedCell}>Choose a KPI:</div>
            {asArray(columns).map((column, columnIndex) => (
              <div key={createNodeKey({ columnIndex })} className={CN.cellHeader}>
                <div> {column} </div>
              </div>
            ))}
          </div>
          <div className={CN.scrollBody} data-bem="InteractiveTable__body">
            {tableData.map((row, rowIndex) => (
              <div
                key={createNodeKey({
                  // TODO: use actual ID instead of index for better React performance
                  index: rowIndex,
                })}
                className={CN.tableRow}
                data-bem="InteractiveTable__row"
              >
                <div
                  className={CN.fixedColumnCell}
                  role="button"
                  tabIndex={0}
                  data-bem="InteractiveTable__fixed-first-cell"
                >
                  <div className={CN.buttonContainer}>
                    {rowIndex !== 0 && (
                      <div className={CN.arrowContainer} onClick={() => handleMoveUp({ rowIndex })}>
                        <img className={CN.arrowUp} src={ICO_UP_DOWN} alt="Move up icon" />
                      </div>
                    )}

                    {USE_RENDER_MOVE_DOWN_BUTTON && (
                      <div className={CN.arrowContainer} onClick={() => handleMoveDn({ rowIndex })}>
                        <img className={CN.arrowDown} src={ICO_UP_DOWN} alt="Move down icon" />
                      </div>
                    )}
                  </div>
                  <div>{row.category}</div>
                </div>
                {row.values.map((value, columnIndex) => (
                  <div
                    key={createNodeKey({
                      // TODO: use actual ID instead of index for better React performance
                      index: columnIndex,
                    })}
                    className={CN.cell}
                    data-bem="InteractiveTable__cell"
                  >
                    {value}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className={CN.tableFooter}>
            <div className={CN.fixedColumnCell}>
              <Button onClick={addNewRow}>Add Row</Button>
            </div>
            <div>{/* NOTE: here we have a footer for now with empty content */}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTable;
